import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { fetchBalances, fetchDelegations } from '@/api'
import CopyWithTooltip from '@/common/CopyWithTooltip'
import ResponsiveModalDrawer from '@/common/ResponsiveModalDrawer'
import ValidatorSelect from '@/common/ValidatorSelect'
import { bridgelessChainInfo } from '@/config'
import { useKeplrWallet } from '@/contexts/KeplrWalletProvider'
import {
  emitter,
  ErrorHandler,
  formatAddress,
  formatBridgeAtomic,
} from '@/helpers'
import { buildDelegateNftMsg, DelegateError } from '@/libs/keplr/messages'
import { invalidateNftScope } from '@/queries/invalidate'
import { queryKeys } from '@/queries/keys'
import UiRing from '@/ui/loaders/UiRing'
import { UiButton } from '@/ui/UiButton'

const delegateSchema = z.object({
  validatorAddress: z.string().min(1, 'Validator is required'),
})

type DelegateFormValues = z.infer<typeof delegateSchema>

function DelegateModal({
  open,
  onClose,
  nftAddress,
}: {
  open: boolean
  onClose: () => void
  nftAddress: string
}) {
  const { signAndBroadcast, address } = useKeplrWallet()
  const queryClient = useQueryClient()

  const form = useForm<DelegateFormValues>({
    resolver: zodResolver(delegateSchema),
    mode: 'onChange',
    defaultValues: {
      validatorAddress: '',
    },
  })

  const {
    handleSubmit,
    watch,
    setValue,
    formState: { isSubmitting, errors },
  } = form

  // Check if NFT already has a delegation (used to gate the Delegate action)
  const { data: delegationData, isLoading: isLoadingDelegation } = useQuery({
    queryKey: queryKeys.staking.delegations(nftAddress),
    enabled: Boolean(open && nftAddress),
    queryFn: async () => {
      return fetchDelegations(nftAddress)
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const hasExistingDelegation = Boolean(
    (delegationData?.delegation_responses ?? []).length > 0,
  )

  // Fetch current spendable balance for this NFT address (abridge)
  const { data: balancesData, isLoading: isLoadingBalances } = useQuery({
    queryKey: queryKeys.bank.balances(nftAddress),
    enabled: Boolean(open && nftAddress),
    queryFn: async () => {
      return fetchBalances(nftAddress)
    },
    staleTime: 30_000,
    refetchInterval: 30_000,
  })

  const availableAmountAtomic = (balancesData?.balances ?? [])
    .filter(b => b.denom === bridgelessChainInfo.stakeCurrency.coinMinimalDenom)
    .map(b => b.amount)
    .filter((v): v is string => typeof v === 'string')
    .reduce<string>((acc, amt) => {
      try {
        return (BigInt(acc || '0') + BigInt(amt || '0')).toString()
      } catch {
        return acc || '0'
      }
    }, '0')

  const delegateTx = useMutation({
    mutationKey: queryKeys.tx.delegate(nftAddress),
    mutationFn: async (values: DelegateFormValues) => {
      if (!address) {
        throw new Error('Wallet not connected')
      }

      if (!availableAmountAtomic || BigInt(availableAmountAtomic) <= 0n) {
        throw new Error('No available balance found for this NFT')
      }

      if (hasExistingDelegation) {
        throw new Error('NFT is already delegated')
      }

      const msg = buildDelegateNftMsg({
        creator: address,
        address: nftAddress,
        validator: values.validatorAddress,
        amount: {
          denom: bridgelessChainInfo.stakeCurrency.coinMinimalDenom,
          amount: availableAmountAtomic,
        },
      })

      return signAndBroadcast([msg])
    },
    onSuccess: txHash => {
      emitter.emit('success', {
        title: 'Delegation sent',
        message: `TxHash: ${formatAddress(txHash)}`,
      })
      invalidateNftScope(queryClient, nftAddress)
      onClose()
    },
    onError: error => {
      ErrorHandler.process(DelegateError.fromUnknown(error))
    },
  })

  const isLoading = isSubmitting || delegateTx.isPending

  return (
    <ResponsiveModalDrawer
      open={open}
      onOpenChange={v => !v && !isLoading && onClose()}
      title={<span className='text-foreground'>Delegate Shard</span>}
      content={
        <form
          onSubmit={handleSubmit(values => delegateTx.mutate(values))}
          className='text-foreground mt-4 flex flex-col gap-4 p-1'
        >
          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Shard Address</span>
            <div className='bg-input flex h-12 items-center gap-2 rounded-lg px-3 py-2'>
              <span className='muted-foreground text-sm'>
                {formatAddress(nftAddress, 20, 15)}
              </span>
              <div className='ml-auto'>
                <CopyWithTooltip value={nftAddress} />
              </div>
            </div>
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Validator</span>
            <ValidatorSelect
              value={watch('validatorAddress')}
              disabled={isLoading}
              triggerClassName='w-full'
              contentClassName='w-full'
              onChange={v =>
                setValue('validatorAddress', v, { shouldValidate: true })
              }
            />
            {errors.validatorAddress && (
              <span className='text-xs text-red-400'>
                {errors.validatorAddress.message as string}
              </span>
            )}
          </div>

          <div className='flex flex-col gap-1'>
            <span className='text-muted-foreground text-xs'>Amount</span>
            <div className='bg-input text-foreground flex h-12 items-center rounded-lg px-3 py-2 text-sm'>
              {balancesData
                ? `${formatBridgeAtomic(availableAmountAtomic)} BRIDGE`
                : '…'}
            </div>
            {balancesData && BigInt(availableAmountAtomic || '0') <= 0n && (
              <span className='text-xs text-red-400'>
                No available balance for this NFT
              </span>
            )}
            {hasExistingDelegation && (
              <span className='text-xs text-red-400'>
                NFT is already delegated. Use Undelegate first.
              </span>
            )}
          </div>
        </form>
      }
      footer={
        <div className='flex w-full gap-2'>
          <UiButton
            variant='outline2'
            className='h-12 flex-1'
            onClick={onClose}
            disabled={isLoading}
          >
            Close
          </UiButton>

          <UiButton
            variant='outline'
            className='h-12 flex-1'
            disabled={
              isLoading ||
              isLoadingDelegation ||
              isLoadingBalances ||
              hasExistingDelegation ||
              BigInt(availableAmountAtomic || '0') <= 0n
            }
            onClick={handleSubmit(values => delegateTx.mutate(values))}
          >
            {isLoading ? (
              <span className='flex items-center gap-2'>
                <UiRing size='15' stroke='2' speed='1.65' color='white' />
                Delegating…
              </span>
            ) : (
              'Delegate'
            )}
          </UiButton>
        </div>
      }
      modalProps={{ className: 'bg-card text-foreground rounded-lg max-w-md' }}
      drawerProps={{ className: 'bg-card text-foreground rounded-lg' }}
    />
  )
}

export default DelegateModal
